import { useEffect } from "react";

import { BannerMode } from "@/types/BannerMode.interface";
import { UNLEVELLED_CONTENT_TYPES } from "@/view/constants/contentTypes/contentTypes.constants";
import UnlevelledContent from "@/view/UnlevelledContent/UnlevelledContent";
import UnlevelledContentProviders from "@/view/UnlevelledContent/utils/UnlevelledContentProviders";
import useReadingTracker from "@/view/utils/useReadingTracker";
import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

/**
 * Normalizes and validates unlevelled content.
 *
 * Ensures the title is populated from link_title if it does not exist,
 * and validates that the content type is a recognized unlevelled content type.
 *
 * @param content - The content to normalize, or null/undefined
 * @returns The normalized content if valid, or null otherwise
 */
export function normalizeUnlevelledContent(
  content: IUnlevelledContent | null | undefined,
): IUnlevelledContent | null {
  if (!content) return null;

  const normalizedContent: IUnlevelledContent = {
    ...content,
    data: {
      ...content.data,
      title: content.data.title || content.data.link_title || "",
    },
  };

  const isValidUnlevelledContent =
    normalizedContent.content_type &&
    UNLEVELLED_CONTENT_TYPES.includes(normalizedContent.content_type);

  return isValidUnlevelledContent ? normalizedContent : null;
}

export interface UnlevelledContentContainerProps {
  bannerMode: BannerMode;
  content: IUnlevelledContent | null | undefined;
}

const UnlevelledContentContainer = ({
  bannerMode,
  content,
}: UnlevelledContentContainerProps) => {
  const unlevelledContent = normalizeUnlevelledContent(content);
  useReadingTracker({ contentId: content?.id });

  useEffect(() => {
    if (unlevelledContent) {
      const { title } = unlevelledContent.data;
      document.title = title;
    }
  }, [unlevelledContent?.data?.title]);

  if (!unlevelledContent) return null;

  return (
    <UnlevelledContentProviders
      contentId={unlevelledContent.id}
      contentType={unlevelledContent.content_type}
      isBannerModeActive={bannerMode.isBannerModeActive}
      isLiteContent={content?.data.is_lite || false}
      isPremiumContent={content?.data.is_premium || false}
    >
      <UnlevelledContent bannerMode={bannerMode} content={unlevelledContent} />
    </UnlevelledContentProviders>
  );
};

export default UnlevelledContentContainer;
